import { useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { TeamLogoSchema } from '../assets/api'
import { PlayerImageSchema } from '../players/api'

export const PlayerRankSchema = z.object({
  player_id: z.number().int().positive(),
  player_name: z.string(),
  player_photo: PlayerImageSchema,
  team_id: z.number().int().positive(),
  team_name: z.string(),
  team_logo: TeamLogoSchema,
  rank: z.number().int().nonnegative(),
  rank_change: z.number().int(),
  team_rank: z.number().int().nonnegative(),
  team_rank_change: z.number().int(),
  points: z.number(),
  rating: z.number(),
  round_points: z.number(),
  round_rating: z.number(),
  month_points: z.number(),
  month_rating: z.number(),
  bonus_points: z.number(),
  bonus_rating: z.number(),
  matches_played: z.number().int().nonnegative(),
})

export const TeamRankSchema = z.object({
  team_id: z.number().int().positive(),
  team_name: z.string(),
  team_logo: TeamLogoSchema,
  division_id: z.number().int().positive().nullable(),
  division_name: z.string().nullable(),
  rank: z.number().int().nonnegative(),
  rank_change: z.number().int(),
  division_rank: z.number().int().nonnegative(),
  division_rank_change: z.number().int(),
  league_rank: z.number().int().nonnegative(),
  league_rank_change: z.number().int(),
  points: z.number(),
  rating: z.number(),
  round_points: z.number(),
  round_rating: z.number(),
  month_points: z.number(),
  month_rating: z.number(),
  bonus_points: z.number(),
  bonus_rating: z.number(),
})

export const PlayerRanksResponseSchema = z.object({
  items: z.array(PlayerRankSchema),
  total: z.number(),
})

export const TeamRanksResponseSchema = z.object({
  items: z.array(TeamRankSchema),
  total: z.number(),
})

export type PlayerRank = z.infer<typeof PlayerRankSchema>
export type TeamRank = z.infer<typeof TeamRankSchema>
export type PlayerRanksResponse = z.infer<typeof PlayerRanksResponseSchema>
export type TeamRanksResponse = z.infer<typeof TeamRanksResponseSchema>

// Tagged union types
export type TeamRanks = { type: 'team'; data: TeamRank[] }
export type PlayerRanks = { type: 'player'; data: PlayerRank[] }

const getPlayerRanks = async ({
  competition_id,
  team_id,
  player_id,
  page,
  page_size,
  search,
}: {
  competition_id: number
  team_id?: number
  player_id?: number
  page?: number
  page_size?: number
  search?: string
}) => {
  const params: Record<string, number | string> = {}
  if (team_id !== undefined) params.team_id = team_id
  if (player_id !== undefined) params.player_id = player_id
  if (page !== undefined) params.page = page
  if (page_size !== undefined) params.page_size = page_size
  if (search !== undefined && search.length >= 3) params.search = search
  const response = await axios.get(
    `/api/competitions/${competition_id}/ranks/players`,
    { params }
  )
  return PlayerRanksResponseSchema.parse(response.data)
}

export const usePlayerRanks = ({
  competition_id,
  team_id,
  player_id,
  page,
  page_size,
  search,
}: {
  competition_id: number
  team_id?: number
  player_id?: number
  page?: number
  page_size?: number
  search?: string
}) => {
  return useSuspenseQuery({
    queryKey: [
      'playerRanks',
      { competition_id, team_id, player_id, page, page_size, search },
    ],
    queryFn: () =>
      getPlayerRanks({
        competition_id,
        team_id,
        player_id,
        page,
        page_size,
        search,
      }),
  })
}

const getTeamRanks = async ({
  competition_id,
  division_id,
  team_id,
  page,
  page_size,
  search,
}: {
  competition_id: number
  division_id?: number
  team_id?: number
  page?: number
  page_size?: number
  search?: string
}) => {
  const params: Record<string, number | string> = {}
  if (division_id !== undefined) params.division_id = division_id
  if (team_id !== undefined) params.team_id = team_id
  if (page !== undefined) params.page = page
  if (page_size !== undefined) params.page_size = page_size
  if (search !== undefined && search.length >= 3) params.search = search
  const response = await axios.get(
    `/api/competitions/${competition_id}/ranks/teams`,
    { params }
  )
  return TeamRanksResponseSchema.parse(response.data)
}

export const useTeamRanks = ({
  competition_id,
  division_id,
  team_id,
  page,
  page_size,
  search,
}: {
  competition_id: number
  division_id?: number
  team_id?: number
  page?: number
  page_size?: number
  search?: string
}) => {
  return useSuspenseQuery({
    queryKey: [
      'teamRanks',
      { competition_id, division_id, team_id, page, page_size, search },
    ],
    queryFn: () =>
      getTeamRanks({
        competition_id,
        division_id,
        team_id,
        page,
        page_size,
        search,
      }),
  })
}
